import { useState, useRef, useEffect } from 'react';
import NoneActionCell from '../../../../components/v2/NoneActionCell.jsx';
import { anchoredPopoverStyle } from '../../../../utils/popoverPosition.js';

/**
 * DeviceCell — shows a device count badge with a hover popover listing device details.
 * Clicking the badge opens the device manager drawer.
 *
 * With zero devices, "None" stays muted but carries the same click action, so
 * assigning a first device does not require another route into the drawer.
 *
 * @param {{ devices: Array, onClick: () => void }} props
 */
const DeviceCell = ({ devices, onClick }) => {
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  const computePosition = () => {
    const style = anchoredPopoverStyle(ref.current);
    if (style) setPopoverStyle(style);
  };

  const handleMouseEnter = () => { clearTimeout(hoverTimeout.current); computePosition(); setOpen(true); };
  const handleMouseLeave = () => { hoverTimeout.current = setTimeout(() => setOpen(false), 100); };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  if (!devices || devices.length === 0) {
    return (
      <NoneActionCell
        className="rp-cell-text--muted"
        onClick={onClick}
        title="Click to manage devices"
      />
    );
  }

  const handleClick = () => {
    setOpen(false);
    if (onClick) onClick();
  };

  return (
    <div className="rp-device-cell" ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        type="button"
        onClick={handleClick}
        className="rp-device-badge"
        aria-label={`${devices.length} device${devices.length !== 1 ? 's' : ''} assigned`}
      >
        <i className="fe fe-tablet" />
        {devices.length}
      </button>

      {open ? (
        <div className="rp-device-popover" role="tooltip" style={popoverStyle}>
          <p className="rp-device-popover__title">
            Assigned Devices ({devices.length})
          </p>
          <ul className="rp-device-popover__list">
            {devices.map((d) => (
              <li key={d.device_id} className="rp-device-popover__item" style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <span className="rp-device-popover__name">{d.device_name}</span>
                {d.inovonics_model_name ? (
                  <span className="rp-device-popover__uuid">{d.inovonics_model_name}</span>
                ) : null}
                {d.descriptions ? (
                  <span className="rp-device-popover__uuid">{d.descriptions}</span>
                ) : null}
                {d.manufacture_uuid ? (
                  <span className="rp-device-popover__uuid">{d.manufacture_uuid}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default DeviceCell;
